import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { format as formatTimeAgo } from 'timeago.js';
import type { Message } from '../types/decision';
import type { Participant } from '../types/participant';
import type { Decision } from '../types/decision';
import { Send } from 'lucide-react';

export interface ChatInterfaceProps {
  decisionId: string;
  participant: Participant | null;
  messages: Message[];
  onSendMessage: (message: string) => Promise<void>;
  onComplete: () => Promise<void>;
  isAiResponding: boolean;
  isSending: boolean;
  isComplete: boolean;
  error: string | null;
  loading?: boolean;
  readOnly?: boolean;
  decision: Decision | null;
}

export function ChatInterface({
  decisionId: _decisionId,
  participant,
  messages,
  onSendMessage,
  onComplete,
  isAiResponding,
  isSending,
  isComplete,
  error: _error,
  loading = false,
  readOnly = false,
  decision: _decision,
}: ChatInterfaceProps) {
  const [newMessage, setNewMessage] = useState('');
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null); // Add this
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const prevMessagesLengthRef = useRef(0);
  const lastReceivedMessageTimestampRef = useRef<number>(Date.now());
  const lastSentMessageTimestampRef = useRef<number>(0); // Add this
  const dailyMessageCountRef = useRef<number>(0); // Add this

  // Add state to track question visibility
  const [isQuestionVisible, setIsQuestionVisible] = useState(true);
  const questionRef = useRef<HTMLDivElement>(null);
  const [showDetails, setShowDetails] = useState(false);


  const formatMessageTimestamp = useCallback((timestamp: Date) => {
    const now = new Date();
    const diffInSeconds = Math.round((now.getTime() - timestamp.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else {
      return formatTimeAgo(timestamp);
    }
  }, []);
  
  // Function to format message text with markdown-style formatting
  const formatMessageText = (text: string): React.ReactNode => {
    // Process bold formatting
    const processBold = (parts: React.ReactNode[]): React.ReactNode[] => {
      return parts.flatMap((part, i) => {
        if (typeof part !== 'string') return part;
        
        const boldParts = part.split(/(\*\*.*?\*\*)/g);
        return boldParts.map((boldPart, index) => {
          if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
            return <strong key={`bold-${i}-${index}`}>{boldPart.slice(2, -2)}</strong>;
          }
          return boldPart;
        });
      });
    };
    
    // Process italics formatting
    const processItalics = (parts: React.ReactNode[]): React.ReactNode[] => {
      return parts.flatMap((part, i) => {
        if (typeof part !== 'string') return part;
        
        const italicParts = part.split(/(\*.*?\*|_.*?_)/g);
        return italicParts.map((italicPart, index) => {
          if ((italicPart.startsWith('*') && italicPart.endsWith('*') && !italicPart.startsWith('**')) || 
              (italicPart.startsWith('_') && italicPart.endsWith('_'))) {
            const cleanText = italicPart.startsWith('*') ? 
              italicPart.slice(1, -1) : italicPart.slice(1, -1);
            return <em key={`italic-${i}-${index}`}>{cleanText}</em>;
          }
          return italicPart;
        });
      });
    };
    
    // Start with the entire text as a single part
    let formattedParts: React.ReactNode[] = [text];
    
    // Apply formatting in sequence
    formattedParts = processBold(formattedParts);
    formattedParts = processItalics(formattedParts);
    
    return formattedParts;
  };

  // Helper function to scroll
  const scrollToBottom = (container: HTMLDivElement, smooth: boolean = false) => {
    container.scrollTo({
      top: container.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto'
    });
  };

  const canComplete = participant?.status === 'active' || participant?.status === 'pending' || participant?.status === 'completed';
      
  // Scroll on new messages or AI response changes
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Only scroll if messages were added or AI state changed
    if (messages.length > 0 && (prevMessagesLengthRef.current < messages.length || isAiResponding)) {
      scrollToBottom(container);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages, isAiResponding]);

  // Focus after receiving a new message
  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender !== 'user' && !readOnly) {
        // Use a small timeout to ensure DOM updates are completed first
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      }
      lastReceivedMessageTimestampRef.current = Date.now();
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages, readOnly]);

  // Keep the focus effect
  useEffect(() => {
    if (!readOnly && !showCompletionMessage && inputRef.current) {
      if (!isAiResponding) {  // Only focus if AI is not currently responding
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.sender !== 'user' && lastReceivedMessageTimestampRef.current + 500 < Date.now()){
          inputRef.current.focus();
        } else if (!lastMessage) {
          inputRef.current.focus();
        }
      }
    }
  }, [readOnly, showCompletionMessage, messages, isAiResponding]);

  // Focus when component mounts or readOnly changes
  useEffect(() => {
    if (!readOnly && inputRef.current) {
      inputRef.current.focus();
    }
  }, [readOnly]);

  // Focus when AI finishes responding
  useEffect(() => {
    if (!isAiResponding && !readOnly && inputRef.current && !showCompletionMessage) {
      // Small delay to ensure UI updates are complete
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [isAiResponding, readOnly, showCompletionMessage]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    // Rate limit: 1 message per 3 seconds
    const now = Date.now();
    if (now - lastSentMessageTimestampRef.current < 3000) {
      setRateLimitError("Please wait a moment before sending another message");
      return;
    }
    
    // Daily message cap (50 messages per day)
    const MAX_DAILY_MESSAGES = 50;
    if (dailyMessageCountRef.current >= MAX_DAILY_MESSAGES) {
      setRateLimitError(`You've reached the daily message limit (${MAX_DAILY_MESSAGES}). Please try again tomorrow.`);
      return;
    }
    
    // Clear any previous error
    setRateLimitError(null);
    
    // Store the message content and reset the input
    const messageContent = newMessage.trim();
    setNewMessage('');
    
    if (inputRef.current) {
      inputRef.current.style.height = '44px';
      
      try {
        inputRef.current.focus(); 
      } catch (e) {
        console.error('Error focusing input:', e);
      }
    }
    
    // Update rate limit tracking
    lastSentMessageTimestampRef.current = now;
    dailyMessageCountRef.current += 1;
    
    // Save the updated count to localStorage
    if (participant) {
      const today = new Date().toISOString().split('T')[0];
      const storageKey = `messageCount_${_decisionId}_${participant.id}_${today}`;
      localStorage.setItem(storageKey, dailyMessageCountRef.current.toString());
    }
    
    // Send the message
    try {
      await onSendMessage(messageContent);
    } catch (error) {
      console.error('Error sending message:', error);
      // Re-focus the input if there was an error
      if (inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      // If there was an error, decrease the message count
      dailyMessageCountRef.current = Math.max(0, dailyMessageCountRef.current - 1);
      if (participant) {
        const today = new Date().toISOString().split('T')[0];
        const storageKey = `messageCount_${_decisionId}_${participant.id}_${today}`;
        localStorage.setItem(storageKey, dailyMessageCountRef.current.toString());
      }
    }
  };

  // Determine the height of the chat container based on participant
  let chatContainerHeight = "h-[calc(100vh-64px)] md:h-[calc(100vh-96px)]";
  if (!participant) {
    chatContainerHeight = "h-auto"; // Auto height when no participant
  }

  // Observe question visibility
  useEffect(() => {
    // Capture the current value of questionRef.current
    const currentQuestionRef = questionRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsQuestionVisible(entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    if (currentQuestionRef) {
      observer.observe(currentQuestionRef);
    }

    return () => {
      if (currentQuestionRef) {
        observer.unobserve(currentQuestionRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!participant) return;
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const storageKey = `messageCount_${_decisionId}_${participant.id}_${today}`;
    const storedCount = localStorage.getItem(storageKey);
    if (storedCount) {
      dailyMessageCountRef.current = parseInt(storedCount, 10);
    }
  }, [_decisionId, participant]);

  return (
    <div className={`flex flex-col relative bg-gray-100/50 md:rounded-lg md:shadow-sm overflow-hidden ${chatContainerHeight}`}>
      {/* Messages Container */}
      <div 
        className="flex-grow overflow-y-auto"
        ref={messagesContainerRef}
      >
        {/* Question Header */}
        <div className="bg-background border-b py-3 px-4 z-10" ref={questionRef}>
          <h2 className="text-2xl font-semibold">{_decision?.question}</h2>
    
          {_decision?.details && (
            <div className="mt-2">
              {showDetails ? (
                <p className="text-sm text-muted-foreground">{_decision.details}</p>
              ) : (
                <button 
                  onClick={() => setShowDetails(true)}
                  className="text-sm text-primary hover:underline focus:outline-none"
                >
                  Show details
                </button>
              )}
              
              {showDetails && (
                <button 
                  onClick={() => setShowDetails(false)}
                  className="text-sm text-primary hover:underline ml-2 focus:outline-none"
                >
                  Hide details
                </button>
              )}
            </div>
          )}
        </div>

        {/* Top Complete Button (Mobile) */}
        {!isComplete && !isQuestionVisible && canComplete && (
          <div className="sticky top-2 z-10 w-full flex justify-center md:hidden px-4 py-2">
            <Button
              onClick={onComplete}
              variant="outline"
              size="default"
              className="text-md px-10 py-6 bg-background shadow-[0_3px_10px_rgba(0,0,0,0.5)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.3)] transition-shadow"
              disabled={isMarkingComplete || isAiResponding || isSending}
            >
              {isMarkingComplete ? 'Updating...' : 'Complete Chat'}
            </Button>
          </div>
        )}

        {/* Messages */}
        <div className="pb-4 md:pb-8">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {loading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    <p>Loading conversation...</p>
                  </div>
                ) : (
                  "No messages yet. Start the conversation!"
                )}
              </div>
            ) : (
              <div className="px-4 py-4 space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${message.sender === 'user'
                        ? 'ml-8 bg-primary text-primary-foreground'
                        : 'mr-8 bg-background border border-input text-foreground'
                      }`}
                  >
                    <p className="text-[15px] leading-relaxed break-words">
                      {formatMessageText(message.content)}
                    </p>
                    <p
                      className={
                        message.sender === 'user'
                          ? 'text-[11px] mt-1 text-primary-foreground/80'
                          : 'text-[11px] mt-1 text-muted-foreground'
                      }
                    >
                      {formatMessageTimestamp(message.timestamp.toDate())}
                    </p>
                  </div>
                </div>
              ))}
              {isAiResponding && (
                <div className="flex justify-start">
                  <div className="mr-8 rounded-2xl px-4 py-3 bg-[#072422] text-white">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce [animation-delay:0.2s]" />
                      <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce [animation-delay:0.4s]" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-background border-t md:left-auto md:right-auto md:max-w-3xl md:mx-auto md:border-x md:rounded-b-lg w-full">
      {!readOnly ? (
        showCompletionMessage ? (
          <div className="p-4 text-center space-y-2">
            <p className="text-green-600 font-medium">Thanks for sharing your views!</p>
            <p className="text-sm text-muted-foreground">
              The group response will be updated to include your input.
            </p>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                {rateLimitError && (
                  <div className="text-destructive text-sm mt-2">
                    {rateLimitError}
                  </div>
                )}
                <textarea
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    e.target.style.height = '44px';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  className="w-full rounded-2xl border bg-background pl-4 pr-16 py-3 text-base md:text-[15px] resize-none min-h-[52px] max-h-32 focus:outline-none focus:ring-2 focus:ring-ring focus:border-input disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isSending || isAiResponding}
                  rows={1}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isSending || isAiResponding || !newMessage.trim()}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-[#072422] text-white disabled:opacity-50 hover:bg-[#072422]/90 transition-colors"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
              {canComplete && (
                <Button
                  onClick={async () => {
                    setIsMarkingComplete(true);
                    try {
                      await onComplete();
                      setShowCompletionMessage(true);
                      setTimeout(() => setShowCompletionMessage(false), 3000);
                    } finally {
                      setIsMarkingComplete(false);
                    }
                  }}
                  variant="secondary"
                  className="whitespace-nowrap hidden md:inline-flex"
                  disabled={isMarkingComplete || isAiResponding || isSending}
                >
                  {isMarkingComplete ? 'Updating...' : 'Complete Chat'}
                </Button>
              )}
            </div>
          </div>
        )
      ) : null}
    </div>
    </div>
  );
}